/* PatientProfile — tabbed accordion-driven self-service profile.
 *
 * iter98.42 — Redesigned to mirror the Doctor Profile structure so the
 * two portals feel like one product. Same building blocks:
 *   • Hero card (gradient patient flavour) with avatar + identity + completeness ring
 *   • Completeness banner under the hero when <100%
 *   • Tab strip in a bordered card
 *   • Tab content uses `ExamAccordion` sections with X/Y fill badges
 *   • Sticky save bar pinned to the bottom (visible only on form tabs)
 *
 * Tabs:
 *   1. Profile       — Personal · Health · Smile goals
 *   2. Care team     — Doctor & clinic (read-only)
 *   3. Account       — Notifications · Security & privacy
 *   4. ID card       — Downloadable patient ID card
 *
 * Save model unchanged: a single PUT /api/auth/profile (+ optional
 * PUT /api/patient/me/smile-goals when the smile-goals modal saves).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Lock, FloppyDisk, Check, Camera, Trash, Phone, EnvelopeSimple,
    MapPin, GenderIntersex, Cake, Globe, Translate, User as UserIcon, Building,
    Clock as ClockIcon, MapTrifold, ChatCircleDots, Pill, Heartbeat, Users,
    Warning, Megaphone, ChartBar, Calendar as CalIcon, Bell, Tooth, Camera as CameraIcon,
    DeviceMobileCamera, ShieldCheck, ShieldWarning, Key, Eye, SignOut,
    CheckCircle, Sparkle, Info, Briefcase, DotsThree, IdentificationCard, Stethoscope,
    PencilSimple,
} from "@phosphor-icons/react";
import { toE164, to10Digit } from "@/lib/phone";
import { toast } from "sonner";
import { ExamAccordion, present } from "@/components/case/DetailedClinicalExam";
import SmileGoalsOnboarding from "@/components/patient/SmileGoalsOnboarding";
import PatientIdCard from "@/components/patient/PatientIdCard";


/* ─────────────────────── Defaults ─────────────────────── */
const EMPTY = {
    name: "", email: "",
    dob: "", gender: "", blood_group: "", preferred_language: "",
    address: "", city: "", state: "", pincode: "", country: "",
    allergies: "", current_medications: "", medical_history: "",
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
    notification_preferences: {},
    photo_data_url: "",
};


// Six toggle rows in the Notifications accordion. Each maps to a single
// boolean in `notification_preferences[key]`. We store the full set as
// a flat object (key → bool) so the PUT /auth/profile payload is small.
const NOTIF_ROWS = [
    { key: "appointment_reminders", label: "Appointment reminders", hint: "24 hrs & 2 hrs before each visit", icon: CalIcon, tone: "primary" },
    { key: "doctor_messages",       label: "Doctor messages",       hint: "Push & email when your doctor replies", icon: ChatCircleDots, tone: "primary" },
    { key: "wear_reminders",        label: "Aligner wear reminders", hint: "Daily nudge at 9 pm if wear is low", icon: Tooth, tone: "indigo" },
    { key: "photo_checkins",        label: "Photo check-in reminders", hint: "Weekly prompt to upload progress photos", icon: CameraIcon, tone: "indigo" },
    { key: "clinic_announcements",  label: "Clinic announcements",  hint: "Promotions, news & holiday hours", icon: Megaphone, tone: "amber", defaultOn: false },
    { key: "weekly_progress",       label: "Weekly progress report", hint: "Email summary every Sunday", icon: ChartBar, tone: "amber" },
];
const DEFAULT_NOTIF_PREFS = NOTIF_ROWS.reduce((acc, r) => {
    acc[r.key] = r.defaultOn !== false;
    return acc;
}, {});


/* Smile-goals display metadata (used by the doctor-mirror card on the
   Profile tab). Same palette/labels as the onboarding modal. */
const SMILE_GOAL_META = {
    aesthetics:      { label: "Aesthetics",      Icon: Sparkle,   tint: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200" },
    functional_bite: { label: "Functional bite", Icon: Tooth,     tint: "bg-cyan-50 text-cyan-700 ring-cyan-200" },
    confidence:      { label: "Confidence",      Icon: Briefcase, tint: "bg-amber-50 text-amber-700 ring-amber-200" },
    other:           { label: "Something else",  Icon: DotsThree, tint: "bg-slate-50 text-slate-700 ring-slate-200" },
};
const SMILE_TIMELINE_META = {
    no_rush:        "No rush — whenever's right",
    within_6m:      "Within 6 months",
    within_year:    "Within a year",
    specific_event: "Has a specific event",
};


/* Tab strip definition — mirrors Doctor Profile's PROFILE_SECTIONS. */
const PROFILE_TABS = [
    { id: "profile",   label: "Profile",   icon: UserIcon },
    { id: "care_team", label: "Care team", icon: Stethoscope },
    { id: "account",   label: "Account",   icon: Bell },
    { id: "id_card",   label: "ID card",   icon: IdentificationCard },
];
const FORM_TABS = new Set(["profile", "account"]);


/* ─────────────────────── Main page ─────────────────────── */
export default function PatientProfile() {
    const { user, setUser, logout } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY);
    const [original, setOriginal] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);
    const [careTeam, setCareTeam] = useState(null);
    const [stats, setStats] = useState({ aligners_done: 0, total_aligners: 0, progress_pct: 0, avg_wear_pct: 0, appointments: 0, days_active: 0 });
    const [activeCaseCode, setActiveCaseCode] = useState(null);
    const [smileGoals, setSmileGoals] = useState(null);
    const [smileGoalsOpen, setSmileGoalsOpen] = useState(false);

    const fileInputRef = useRef(null);
    const [pwdOpen, setPwdOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [logoutOpen, setLogoutOpen] = useState(false);

    /* Tab state — backs the tab strip. `?tab=…` deep-links work so a
       refresh / share / bookmark restores the same view. */
    const [searchParams, setSearchParams] = useSearchParams();
    const allowedTabs = new Set(PROFILE_TABS.map((t) => t.id));
    const urlTab = searchParams.get("tab");
    const [tab, setTabState] = useState(allowedTabs.has(urlTab) ? urlTab : "profile");
    const setTab = (next) => {
        setTabState(next);
        const params = new URLSearchParams(searchParams);
        if (next === "profile") params.delete("tab");
        else params.set("tab", next);
        setSearchParams(params, { replace: true });
    };

    /* Per-tab single-open accordion state. Mirrors Doctor Profile's
       openProfileAcc / openAccountAcc pattern. */
    const [openProfileAcc, setOpenProfileAcc] = useState(null);
    const [openAccountAcc, setOpenAccountAcc] = useState(null);

    /* Load everything in parallel — auth, care team, aligner progress,
       appointments count, and the smile-goals blob (for the Profile tab
       smile-goals accordion). */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [meR, ctR, apR, apptR, sgR] = await Promise.allSettled([
                    api.get("/auth/me"),
                    api.get("/patient/me/care-team"),
                    api.get("/patient/me/aligner-progress"),
                    api.get("/appointments/my"),
                    api.get("/patient/me/smile-goals"),
                ]);
                if (cancelled) return;

                if (meR.status === "fulfilled") {
                    const d = meR.value.data;
                    const next = {
                        ...EMPTY,
                        name: d.name || "", email: d.email || "",
                        dob: d.dob || "", gender: d.gender || "",
                        blood_group: d.blood_group || "",
                        preferred_language: d.preferred_language || "",
                        address: d.address || "", city: d.city || "",
                        state: d.state || "", pincode: d.pincode || "",
                        country: d.country || "",
                        allergies: d.allergies || "",
                        current_medications: d.current_medications || "",
                        medical_history: d.medical_history || "",
                        emergency_contact_name: d.emergency_contact_name || "",
                        emergency_contact_phone: to10Digit(d.emergency_contact_phone || ""),
                        emergency_contact_relation: d.emergency_contact_relation || "",
                        notification_preferences: { ...DEFAULT_NOTIF_PREFS, ...(d.notification_preferences || {}) },
                        photo_data_url: d.photo_data_url || "",
                    };
                    setForm(next); setOriginal(next);

                    if (d.created_at) {
                        const ms = Date.now() - new Date(d.created_at).getTime();
                        setStats((s) => ({ ...s, days_active: Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24))) }));
                    }
                }

                if (ctR.status === "fulfilled") setCareTeam(ctR.value.data);

                if (apR.status === "fulfilled") {
                    const ap = apR.value.data;
                    if (ap?.active && ap.stage) {
                        const total = Number(ap.stage.total || 0);
                        const cur = Number(ap.stage.current || 0);
                        const avg7d = Number(ap.wear?.avg_7d || 0);
                        const target = Number(ap.wear?.target_hours || 22);
                        setStats((s) => ({
                            ...s,
                            aligners_done: cur,
                            total_aligners: total,
                            progress_pct: total > 0 ? Math.round((cur / total) * 100) : 0,
                            avg_wear_pct: target > 0 ? Math.min(100, Math.round((avg7d / target) * 100)) : 0,
                        }));
                        if (ap.case?.code) setActiveCaseCode(ap.case.code);
                    } else if (ap?.case?.code) {
                        setActiveCaseCode(ap.case.code);
                    }
                }

                if (apptR.status === "fulfilled") {
                    setStats((s) => ({ ...s, appointments: (apptR.value.data || []).length }));
                }

                if (sgR.status === "fulfilled") setSmileGoals(sgR.value.data);
            } finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, []);

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);

    const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const onNotif = (key, value) => {
        setForm((f) => ({ ...f, notification_preferences: { ...(f.notification_preferences || {}), [key]: value } }));
    };

    const save = async () => {
        if ((form.name || "").trim().length < 2) { toast.error("Name is required"); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim() || null,
                dob: form.dob || null,
                gender: form.gender || null,
                blood_group: form.blood_group || null,
                preferred_language: form.preferred_language || null,
                address: form.address.trim() || null,
                city: form.city.trim() || null,
                state: form.state.trim() || null,
                pincode: form.pincode.trim() || null,
                allergies: form.allergies.trim() || null,
                current_medications: form.current_medications.trim() || null,
                medical_history: form.medical_history.trim() || null,
                emergency_contact_name: form.emergency_contact_name.trim() || null,
                emergency_contact_phone: form.emergency_contact_phone ? (toE164(form.emergency_contact_phone) || form.emergency_contact_phone.trim()) : null,
                emergency_contact_relation: form.emergency_contact_relation.trim() || null,
                notification_preferences: form.notification_preferences,
            };
            const { data } = await api.put("/auth/profile", payload);
            setUser(data);
            setOriginal({ ...form });
            toast.success("All changes saved");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save your details");
        } finally { setSaving(false); }
    };

    const discard = () => {
        setForm(original);
        toast("Changes discarded");
    };

    /* Photo pipeline — client-side resize → PUT /auth/profile pattern. */
    const resizePhoto = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = 256; canvas.height = 256;
                const ctx = canvas.getContext("2d");
                const srcSize = Math.min(img.width, img.height);
                const sx = (img.width - srcSize) / 2;
                const sy = (img.height - srcSize) / 2;
                ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, 256, 256);
                resolve(canvas.toDataURL("image/jpeg", 0.85));
            };
            img.onerror = () => reject(new Error("Not a valid image"));
            img.src = reader.result;
        };
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsDataURL(file);
    });

    const pickPhoto = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
        if (file.size > 8 * 1024 * 1024) { toast.error("Image too large — keep it under 8MB"); return; }
        setPhotoBusy(true);
        try {
            const dataUrl = await resizePhoto(file);
            const { data } = await api.put("/auth/profile", { photo_data_url: dataUrl });
            setUser(data);
            setForm((f) => ({ ...f, photo_data_url: data.photo_data_url || dataUrl }));
            setOriginal((o) => ({ ...o, photo_data_url: data.photo_data_url || dataUrl }));
            toast.success("Photo updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Photo upload failed");
        } finally { setPhotoBusy(false); }
    };

    /* Fill ratios per accordion — drives the X/Y badge in the trigger.
       Smile goals counts towards 4 (primary_goal, timeline, concerns,
       prior_treatment). Booleans (prior_treatment=false counts) are
       handled separately since `present` excludes them. */
    const fills = useMemo(() => {
        const c = (...vals) => vals.filter(present).length;
        const sgFilled = (() => {
            if (!smileGoals) return 0;
            return (smileGoals.primary_goal ? 1 : 0)
                + (smileGoals.timeline ? 1 : 0)
                + (smileGoals.concerns ? 1 : 0)
                + (smileGoals.prior_treatment === true || smileGoals.prior_treatment === false ? 1 : 0);
        })();
        return {
            personal: { filled: c(form.name, form.email, form.dob, form.gender, form.city, form.preferred_language), total: 6 },
            health:   { filled: c(form.allergies, form.current_medications, form.medical_history, form.emergency_contact_name, form.emergency_contact_phone), total: 5 },
            smile:    { filled: sgFilled, total: 4 },
        };
    }, [form, smileGoals]);

    /* Overall completeness across every accordion that has a fill ratio.
       Powers the right-side ring in the hero + the teal nudge banner. */
    const overallCompletion = useMemo(() => {
        const sections = Object.values(fills);
        const totalFields = sections.reduce((s, f) => s + (f.total || 0), 0);
        const filledFields = sections.reduce((s, f) => s + (f.filled || 0), 0);
        if (totalFields === 0) return { pct: 0, gaps: 0 };
        const gaps = sections.filter((f) => f.total > 0 && f.filled < f.total).length;
        return { pct: Math.round((filledFields / totalFields) * 100), gaps };
    }, [fills]);
    const completionPct = overallCompletion.pct;

    if (loading) {
        return <div className="p-8 text-sm text-muted-foreground" data-testid="patient-profile-loading">Loading profile…</div>;
    }

    const initials = (form.name || "").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
    const sinceLabel = user?.created_at ? new Date(user.created_at).toLocaleDateString([], { month: "long", year: "numeric" }) : null;

    return (
        <>
        <div className="space-y-5 max-w-5xl pb-24" data-testid="patient-profile-page">

            {/* ── Page header ── */}
            <div>
                <h1 className="font-display text-2xl font-bold tracking-tight">My Profile</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Manage your personal details, health info, smile goals &amp; preferences.
                </p>
            </div>

            {/* ── Hero card ── gradient banner + identity strip + completeness ring */}
            <div
                className="relative overflow-hidden rounded-2xl px-6 py-6 flex flex-col md:flex-row gap-6 items-start"
                style={{ background: "linear-gradient(135deg, hsl(193 100% 22%), hsl(192 100% 31%), hsl(173 58% 42%))" }}
                data-testid="profile-hero"
            >
                <div className="absolute top-[-50px] right-[-50px] w-[220px] h-[220px] rounded-full bg-white/[0.06] pointer-events-none" />
                <div className="absolute bottom-[-60px] left-[35%] w-[160px] h-[160px] rounded-full bg-white/[0.04] pointer-events-none" />

                {/* Avatar */}
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="relative shrink-0 group z-10"
                    data-testid="hero-avatar-btn"
                    disabled={photoBusy}
                >
                    {form.photo_data_url ? (
                        <img
                            src={form.photo_data_url}
                            alt={form.name}
                            className="w-[88px] h-[88px] rounded-full object-cover border-[2.5px] border-white/40"
                        />
                    ) : (
                        <div className="w-[88px] h-[88px] rounded-full bg-white/20 border-[2.5px] border-white/40 grid place-items-center text-white text-3xl font-bold">
                            {initials}
                        </div>
                    )}
                    <span className="absolute bottom-0 right-0 w-[24px] h-[24px] rounded-full bg-white border-2 border-white/60 grid place-items-center text-primary shadow-sm">
                        <Camera size={12} weight="fill" />
                    </span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) pickPhoto(f); e.target.value = ""; }}
                        data-testid="profile-photo-input"
                    />
                </button>

                {/* Identity strip */}
                <div className="flex-1 min-w-0 space-y-1.5 relative z-10">
                    <h2 className="text-white text-[22px] font-semibold tracking-tight truncate">{form.name || "Welcome"}</h2>
                    <div className="flex flex-wrap gap-3 text-[12px] text-white/85">
                        {form.email && <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={12} weight="bold" /> {form.email}</span>}
                        {user?.phone && <span className="inline-flex items-center gap-1.5"><Phone size={12} weight="bold" /> +91 {to10Digit(user.phone)}</span>}
                        {(form.city || form.country) && (
                            <span className="inline-flex items-center gap-1.5"><MapPin size={12} weight="bold" /> {[form.city, form.country].filter(Boolean).join(", ")}</span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                        <span className="inline-flex items-center gap-1 bg-white/15 border border-white/25 rounded-full px-2.5 py-0.5 text-[11px] font-medium text-white">
                            <ShieldCheck size={11} weight="fill" /> Verified patient
                        </span>
                        {stats.total_aligners > 0 && (
                            <span className="inline-flex items-center gap-1 bg-white/15 border border-white/25 rounded-full px-2.5 py-0.5 text-[11px] font-medium text-white">
                                <Tooth size={11} weight="fill" /> Active treatment
                            </span>
                        )}
                        {sinceLabel && (
                            <span className="inline-flex items-center gap-1 bg-white/15 border border-white/25 rounded-full px-2.5 py-0.5 text-[11px] font-medium text-white">
                                <CalIcon size={11} weight="bold" /> Patient since {sinceLabel}
                            </span>
                        )}
                    </div>
                </div>

                {/* Completeness ring */}
                <div className="shrink-0 flex flex-col items-center gap-1.5 relative z-10" data-testid="profile-completeness-ring">
                    <CompletenessRing pct={completionPct} />
                    <div className="text-[10px] text-white/85 font-medium text-center leading-tight">Profile<br/>complete</div>
                </div>
            </div>

            {/* ── Stat strip ── */}
            <div className="flex gap-2 flex-wrap" data-testid="profile-stat-strip">
                <StatChip value={stats.aligners_done} label="Aligners done" tone="primary" />
                <StatChip value={stats.total_aligners} label="Total aligners" />
                <StatChip value={`${stats.progress_pct}%`} label="Progress" tone="primary" />
                <StatChip value={`${stats.avg_wear_pct}%`} label="Avg. wear" tone="indigo" />
                <StatChip value={stats.appointments} label="Appointments" />
                <StatChip value={stats.days_active} label="Days active" />
            </div>

            {/* ── Completeness banner — only when below 100% ── */}
            {completionPct < 100 && (
                <div
                    className="rounded-xl bg-primary/10 border border-primary/30 px-4 py-3 flex items-center gap-3"
                    data-testid="profile-completeness-banner"
                >
                    <Info size={18} weight="duotone" className="text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-primary">Complete your profile so your doctor has the full picture</div>
                        <div className="text-[11px] text-primary/80 mt-0.5">
                            {overallCompletion.gaps > 0
                                ? `${overallCompletion.gaps} section${overallCompletion.gaps === 1 ? "" : "s"} still ${overallCompletion.gaps === 1 ? "has" : "have"} gaps. Fill in the highlighted ones below.`
                                : "Add the remaining details below."}
                        </div>
                    </div>
                    <div className="font-display text-xl font-bold text-primary tabular-nums shrink-0">{completionPct}%</div>
                </div>
            )}

            {/* ── Tab strip — wrapped in bordered card to match Doctor Profile ── */}
            <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="profile-tab-strip-wrap">
                <div className="flex items-center gap-0 overflow-x-auto" data-testid="profile-tab-strip">
                    {PROFILE_TABS.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => setTab(s.id)}
                            data-testid={`profile-tab-${s.id}`}
                            className={`flex items-center gap-2 px-4 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                                tab === s.id
                                    ? "border-primary text-primary"
                                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/40"
                            }`}
                        >
                            <s.icon size={14} weight={tab === s.id ? "duotone" : "regular"} />
                            {s.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Tab content ── */}
            <div data-testid="profile-form" className="rounded-2xl border border-border bg-card p-6">

            {/* PROFILE TAB — Personal · Health · Smile goals */}
            {tab === "profile" && (
                <div className="space-y-2.5">
                    <ExamAccordion
                        id="personal"
                        title="Personal details"
                        sub="Shared only with your treating doctor"
                        filled={fills.personal.filled}
                        total={fills.personal.total}
                        icon={<UserIcon size={16} weight="duotone" />}
                        tone="indigo"
                        open={openProfileAcc === "personal"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "personal" : null)}
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <FieldLine label="Full name *">
                                <Input value={form.name} onChange={(e) => onField("name", e.target.value)} className="h-10" data-testid="profile-name" />
                            </FieldLine>
                            <FieldLine label="Email">
                                <Input value={form.email} onChange={(e) => onField("email", e.target.value)} type="email" className="h-10" data-testid="profile-email" />
                            </FieldLine>
                            <FieldLine label="Phone" hint="Locked — login identifier">
                                <Input value={user?.phone ? `+91 ${to10Digit(user.phone)}` : ""} disabled className="h-10" data-testid="profile-phone" />
                            </FieldLine>
                            <FieldLine label="Date of birth" hint="Clinic-managed">
                                <Input
                                    value={form.dob ? new Date(form.dob).toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" }) : ""}
                                    disabled
                                    className="h-10"
                                    data-testid="profile-dob"
                                />
                            </FieldLine>
                            <FieldLine label="Gender" hint="Clinic-managed">
                                <Input
                                    value={form.gender ? form.gender.charAt(0).toUpperCase() + form.gender.slice(1).replace(/_/g, " ") : ""}
                                    disabled
                                    className="h-10"
                                    data-testid="profile-gender"
                                />
                            </FieldLine>
                            <FieldLine label="City">
                                <Input value={form.city} onChange={(e) => onField("city", e.target.value)} className="h-10" data-testid="profile-city" />
                            </FieldLine>
                            <FieldLine label="Country" hint="Clinic-managed">
                                <Input value={form.country} disabled className="h-10" data-testid="profile-country" />
                            </FieldLine>
                            <FieldLine label="Preferred language">
                                <Input value={form.preferred_language} onChange={(e) => onField("preferred_language", e.target.value)} placeholder="English" className="h-10" data-testid="profile-language" />
                            </FieldLine>
                        </div>
                        <div className="mt-4 flex items-start gap-2 text-[11px] text-muted-foreground bg-secondary/40 rounded-lg p-2.5">
                            <Lock size={12} className="mt-0.5 shrink-0" />
                            <span>Locked fields (DOB, gender, country, phone) can only be changed by contacting your clinic.</span>
                        </div>
                    </ExamAccordion>

                    <ExamAccordion
                        id="health"
                        title="Health information"
                        sub={careTeam?.doctor ? `Shared with ${careTeam.doctor.name} to ensure your safest care` : "Shared with your treating doctor"}
                        filled={fills.health.filled}
                        total={fills.health.total}
                        icon={<Heartbeat size={16} weight="duotone" />}
                        tone="rose"
                        open={openProfileAcc === "health"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "health" : null)}
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <FieldLine label="Allergies" hint="Comma separated">
                                <Input value={form.allergies} onChange={(e) => onField("allergies", e.target.value)} placeholder="e.g. Penicillin, latex" className="h-10" data-testid="profile-allergies" />
                            </FieldLine>
                            <FieldLine label="Current medications">
                                <Input value={form.current_medications} onChange={(e) => onField("current_medications", e.target.value)} placeholder="List any drugs you take" className="h-10" data-testid="profile-meds" />
                            </FieldLine>
                            <FieldLine label="Notable conditions" hint="Diabetes, hypertension, prior ortho, etc.">
                                <Input value={form.medical_history} onChange={(e) => onField("medical_history", e.target.value)} placeholder="Any medical history" className="h-10" data-testid="profile-conditions" />
                            </FieldLine>
                            <FieldLine label="Blood group" hint="Optional">
                                <Input value={form.blood_group} disabled className="h-10" data-testid="profile-blood-group" />
                            </FieldLine>
                        </div>
                        <div className="mt-4 pt-4 border-t border-dashed border-border/60">
                            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                                <Users size={11} weight="duotone" /> Emergency contact
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <FieldLine label="Name">
                                    <Input value={form.emergency_contact_name} onChange={(e) => onField("emergency_contact_name", e.target.value)} placeholder="Full name" className="h-10" data-testid="profile-ec-name" />
                                </FieldLine>
                                <FieldLine label="Phone" hint="10-digit Indian number">
                                    <Input
                                        value={form.emergency_contact_phone}
                                        onChange={(e) => onField("emergency_contact_phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                                        inputMode="numeric"
                                        placeholder="9999999999"
                                        className="h-10 font-mono"
                                        data-testid="profile-ec-phone"
                                    />
                                </FieldLine>
                                <FieldLine label="Relationship">
                                    <Input value={form.emergency_contact_relation} onChange={(e) => onField("emergency_contact_relation", e.target.value)} placeholder="e.g. Mother, Spouse" className="h-10" data-testid="profile-ec-relation" />
                                </FieldLine>
                            </div>
                        </div>
                    </ExamAccordion>

                    <ExamAccordion
                        id="smile"
                        title="Smile goals"
                        sub="What you told your doctor about your treatment expectations"
                        filled={fills.smile.filled}
                        total={fills.smile.total}
                        icon={<Sparkle size={16} weight="duotone" />}
                        tone="violet"
                        open={openProfileAcc === "smile"}
                        onOpenChange={(o) => setOpenProfileAcc(o ? "smile" : null)}
                    >
                        <SmileGoalsView
                            goals={smileGoals}
                            onEdit={() => setSmileGoalsOpen(true)}
                        />
                    </ExamAccordion>
                </div>
            )}

            {/* CARE TEAM TAB */}
            {tab === "care_team" && (
                <Section
                    title="My doctor & clinic"
                    desc={activeCaseCode ? `Your assigned doctor for Case #${activeCaseCode}.` : "Your assigned doctor for treatment & care."}
                    testId="section-care-team"
                >
                    {careTeam?.doctor ? (
                        <DoctorClinicCard doctor={careTeam.doctor} />
                    ) : (
                        <div className="max-w-md mx-auto py-10 text-center" data-testid="care-team-empty">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary mb-4">
                                <UserIcon size={26} weight="duotone" className="text-muted-foreground" />
                            </div>
                            <h3 className="font-display text-base font-bold">No care team assigned yet</h3>
                            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                Once your clinic links you to a treating doctor, their contact details &amp; clinic info will appear here.
                            </p>
                        </div>
                    )}
                </Section>
            )}

            {/* ACCOUNT TAB — Notifications · Security */}
            {tab === "account" && (
                <div className="space-y-2.5">
                    <ExamAccordion
                        id="notifications"
                        title="Notification preferences"
                        sub="Control how Invisique™ contacts you"
                        icon={<Bell size={16} weight="duotone" />}
                        tone="orange"
                        open={openAccountAcc === "notifications"}
                        onOpenChange={(o) => setOpenAccountAcc(o ? "notifications" : null)}
                    >
                        <NotificationsEditor value={form.notification_preferences} onChange={onNotif} />
                    </ExamAccordion>

                    <ExamAccordion
                        id="security"
                        title="Security &amp; privacy"
                        sub="Account protection & data ownership"
                        icon={<Lock size={16} weight="duotone" />}
                        tone="slate"
                        open={openAccountAcc === "security"}
                        onOpenChange={(o) => setOpenAccountAcc(o ? "security" : null)}
                    >
                        <SecurityPanel
                            onChangePassword={() => setPwdOpen(true)}
                            onDeleteAccount={() => setDeleteOpen(true)}
                            onSignOut={() => setLogoutOpen(true)}
                        />
                    </ExamAccordion>
                </div>
            )}

            {/* ID CARD TAB */}
            {tab === "id_card" && (
                <Section
                    title="Patient member card"
                    desc="Premium card with a QR your clinic can scan at reception."
                    testId="section-id-card"
                >
                    <PatientIdCard form={form} user={user} careTeam={careTeam} caseCode={activeCaseCode} />
                </Section>
            )}

            </div>
        </div>

        {/* iter98.42 — Sticky save bar pinned to the bottom. Mirrors the
            Doctor Profile pattern; visible only on form tabs (Profile,
            Account). Offset by the desktop sidebar width (md:left-64). */}
        {FORM_TABS.has(tab) && (
            <div
                className="fixed bottom-0 left-0 md:left-64 right-0 bg-card border-t border-border px-6 py-3 flex items-center justify-between gap-3 z-30 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
                data-testid="profile-save-bar"
            >
                <div className="text-xs font-medium" data-testid="profile-save-status">
                    {isDirty ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-700">
                            <Warning size={13} weight="fill" /> Unsaved changes
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-primary">
                            <Check size={13} weight="bold" /> All saved
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={discard}
                        disabled={saving || !isDirty}
                        className="rounded-full"
                        data-testid="patient-profile-discard-btn"
                    >
                        Discard
                    </Button>
                    <Button
                        size="sm"
                        onClick={save}
                        disabled={saving || !isDirty}
                        className="rounded-full"
                        data-testid="patient-profile-save-btn"
                    >
                        <FloppyDisk size={13} className="mr-1.5" weight="bold" />
                        {saving ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </div>
        )}

        {/* Modals */}
        <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
        <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
        <SignOutDialog open={logoutOpen} onClose={() => setLogoutOpen(false)} onConfirm={() => { setLogoutOpen(false); logout?.(); navigate("/login"); }} />
        <SmileGoalsOnboarding
            open={smileGoalsOpen}
            onClose={(_completed) => {
                setSmileGoalsOpen(false);
                api.get("/patient/me/smile-goals").then((r) => setSmileGoals(r.data)).catch(() => null);
            }}
        />
        </>
    );
}


/* ─────────────────────── Sub-components ─────────────────────── */

/* Section primitive used for non-accordion tabs (Care team · ID card). */
function Section({ title, desc, right, testId, children }) {
    return (
        <section data-testid={testId}>
            <div className="flex items-center justify-between gap-2 mb-1">
                <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
                {right}
            </div>
            {desc && <p className="text-xs text-muted-foreground mb-4">{desc}</p>}
            {children}
        </section>
    );
}


function FieldLine({ label, hint, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold flex items-center gap-1.5">
                {label}
                {hint && <span className="text-[10px] font-normal text-muted-foreground italic">· {hint}</span>}
            </Label>
            <div className="mt-1">{children}</div>
        </div>
    );
}


/* Smile-goals read view shown inside the Profile tab's Smile goals
   accordion. Mirrors the Doctor's SmileGoalsIntakeCard so what the
   patient sees here matches what the doctor reads on their end. */
function SmileGoalsView({ goals, onEdit }) {
    const hasAnyData = goals && (
        goals.primary_goal
        || goals.concerns
        || goals.timeline
        || goals.prior_treatment === true
        || goals.prior_treatment === false
    );

    if (!hasAnyData) {
        return (
            <div className="text-center py-6" data-testid="smile-goals-empty">
                <div className="w-14 h-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-3">
                    <Sparkle size={22} className="text-primary" weight="duotone" />
                </div>
                <p className="text-sm font-semibold">Share your smile goals</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                    Takes 30 seconds. Helps your doctor walk into your first visit prepared with the right plan.
                </p>
                <Button size="sm" className="mt-4 rounded-full" onClick={onEdit} data-testid="smile-goals-start-btn">
                    Tell us about your smile <ArrowRightArrow />
                </Button>
            </div>
        );
    }

    const goalKey = goals.primary_goal || "other";
    const goalMeta = SMILE_GOAL_META[goalKey] || SMILE_GOAL_META.other;
    const GoalIcon = goalMeta.Icon;
    const eventDate = goals.timeline_event_date
        ? new Date(goals.timeline_event_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        : null;
    const submittedAt = goals.completed_at || goals.updated_at;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    {goals.completed_at && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                            <CheckCircle size={10} weight="fill" /> Submitted
                        </span>
                    )}
                    {submittedAt && (
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                            {new Date(submittedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                    )}
                </div>
                <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={onEdit} data-testid="smile-goals-edit-btn">
                    <PencilSimple size={12} className="mr-1.5" /> Re-do
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {goals.primary_goal && (
                    <div data-testid="smile-goals-primary">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Primary goal</div>
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${goalMeta.tint}`}>
                            <GoalIcon size={12} weight="fill" />
                            {goalMeta.label}
                        </div>
                        {goals.primary_goal === "other" && goals.primary_goal_other && (
                            <div className="text-xs text-foreground/80 italic mt-2 pl-1">&ldquo;{goals.primary_goal_other}&rdquo;</div>
                        )}
                    </div>
                )}
                {goals.timeline && (
                    <div data-testid="smile-goals-timeline">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Timeline</div>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary ring-1 ring-primary/20">
                            <CalIcon size={12} weight="fill" />
                            {SMILE_TIMELINE_META[goals.timeline] || goals.timeline}
                        </div>
                        {goals.timeline === "specific_event" && eventDate && (
                            <div className="text-xs text-foreground/80 mt-2 pl-1">Event date: <span className="font-semibold">{eventDate}</span></div>
                        )}
                    </div>
                )}
                {goals.concerns && (
                    <div className="sm:col-span-2" data-testid="smile-goals-concerns">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">What bothers you most</div>
                        <p className="text-sm text-foreground/85 leading-relaxed italic">&ldquo;{goals.concerns}&rdquo;</p>
                    </div>
                )}
                {(goals.prior_treatment === true || goals.prior_treatment === false) && (
                    <div className="sm:col-span-2" data-testid="smile-goals-prior">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Prior ortho treatment</div>
                        <div className="flex items-start gap-2 text-sm">
                            {goals.prior_treatment ? (
                                <>
                                    <CheckCircle size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                                    <div>
                                        <span className="font-semibold text-amber-800">Yes</span>
                                        {goals.prior_treatment_notes && (
                                            <span className="text-foreground/80"> — {goals.prior_treatment_notes}</span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Info size={14} weight="duotone" className="text-muted-foreground mt-0.5 shrink-0" />
                                    <span className="font-semibold">No — first time</span>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ArrowRightArrow() {
    return <span aria-hidden className="inline-block ml-1">→</span>;
}


/* Notifications editor — 6-row toggle stack, drives notification_preferences. */
const NOTIF_TONE_CLS = {
    primary: "bg-primary/10 text-primary",
    indigo: "bg-indigo-100 text-indigo-700",
    amber: "bg-amber-100 text-amber-700",
};
function NotificationsEditor({ value, onChange }) {
    return (
        <div className="space-y-1" data-testid="patient-notif-editor">
            {NOTIF_ROWS.map((row, i) => {
                const Icon = row.icon;
                const on = value?.[row.key] ?? (row.defaultOn !== false);
                return (
                    <div
                        key={row.key}
                        className={`flex items-center justify-between gap-3 py-3 ${i < NOTIF_ROWS.length - 1 ? "border-b border-border/60" : ""}`}
                        data-testid={`notif-row-${row.key}`}
                    >
                        <div className="flex items-start gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-md grid place-items-center shrink-0 ${NOTIF_TONE_CLS[row.tone]}`}>
                                <Icon size={15} weight="duotone" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[13px] font-medium truncate">{row.label}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{row.hint}</div>
                            </div>
                        </div>
                        <Switch
                            checked={!!on}
                            onCheckedChange={(v) => onChange(row.key, v)}
                            data-testid={`notif-${row.key}-toggle`}
                        />
                    </div>
                );
            })}
        </div>
    );
}


/* Security panel — password, 2FA (on by default), sessions placeholder,
   danger zone, sign-out link. */
function SecurityPanel({ onChangePassword, onDeleteAccount, onSignOut }) {
    return (
        <div className="space-y-4" data-testid="patient-security-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="border border-border rounded-xl p-4" data-testid="security-tile-password">
                    <div className="w-8 h-8 rounded-md bg-primary/10 text-primary grid place-items-center mb-2.5"><Lock size={15} weight="duotone" /></div>
                    <div className="text-[13px] font-medium">Password</div>
                    <div className="text-[11px] text-muted-foreground mb-3">Sign-in OTP is the default. Set an optional backup password.</div>
                    <Button size="sm" variant="outline" className="w-full rounded-md text-xs" onClick={onChangePassword} data-testid="change-password-btn">
                        <Key size={13} className="mr-1.5" /> Change password
                    </Button>
                </div>
                <div className="border border-border rounded-xl p-4" data-testid="security-tile-2fa">
                    <div className="w-8 h-8 rounded-md bg-indigo-100 text-indigo-700 grid place-items-center mb-2.5"><ShieldCheck size={15} weight="duotone" /></div>
                    <div className="text-[13px] font-medium">Two-factor auth</div>
                    <div className="text-[11px] text-muted-foreground mb-3">SMS OTP every sign-in</div>
                    <div className="flex items-center gap-2.5">
                        <Switch checked disabled data-testid="2fa-toggle" />
                        <span className="text-[12px] text-primary font-medium">Enabled</span>
                    </div>
                </div>
                <div className="border border-border rounded-xl p-4" data-testid="security-tile-sessions">
                    <div className="w-8 h-8 rounded-md bg-amber-100 text-amber-700 grid place-items-center mb-2.5"><DeviceMobileCamera size={15} weight="duotone" /></div>
                    <div className="text-[13px] font-medium">Active sessions</div>
                    <div className="text-[11px] text-muted-foreground mb-3">1 device currently signed in</div>
                    <Button size="sm" variant="outline" className="w-full rounded-md text-xs" onClick={() => toast("Active sessions coming soon")} data-testid="view-sessions-btn">
                        <Eye size={13} className="mr-1.5" /> View sessions
                    </Button>
                </div>
            </div>

            <div className="flex gap-2.5 items-start p-3 rounded-md bg-rose-50 border border-rose-200">
                <Warning size={15} className="text-rose-700 mt-0.5 shrink-0" />
                <p className="text-[12px] leading-relaxed text-rose-700">
                    <strong>Danger zone:</strong> Deleting your account permanently removes all your data including treatment history, photos and appointments. This action cannot be undone.
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onDeleteAccount}
                    className="border-rose-300 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-md"
                    data-testid="delete-account-btn"
                >
                    <Trash size={13} className="mr-1.5" /> Delete my account
                </Button>
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={onSignOut}
                    className="rounded-md text-muted-foreground"
                    data-testid="signout-btn"
                >
                    <SignOut size={13} className="mr-1.5" /> Sign out
                </Button>
            </div>
        </div>
    );
}


/* Stat chip used in the strip under the hero. */
function StatChip({ value, label, tone }) {
    const cls = tone === "primary" ? "text-primary" : tone === "indigo" ? "text-indigo-600" : "text-foreground";
    return (
        <div className="flex-1 min-w-[100px] bg-card border border-border rounded-xl py-3 px-3 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`text-xl font-bold tabular-nums ${cls}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
        </div>
    );
}


/* Completeness ring — same SVG pattern as Doctor Profile. White-on-hero
   variant uses inherited color via the CSS currentColor trick. */
function CompletenessRing({ pct }) {
    const safePct = Math.max(0, Math.min(100, Math.round(pct || 0)));
    const r = 28;
    const C = 2 * Math.PI * r;
    const offset = C * (1 - safePct / 100);
    return (
        <div className="relative w-[72px] h-[72px]" data-testid="profile-completeness-ring-svg">
            <svg width="72" height="72" viewBox="0 0 72 72" className="-rotate-90">
                <circle cx="36" cy="36" r={r} fill="none" stroke="currentColor" strokeWidth="6" className="text-white/25" />
                <circle
                    cx="36" cy="36" r={r}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={C}
                    strokeDashoffset={offset}
                    className="text-white transition-all"
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white tabular-nums" data-testid="profile-completeness-pct">
                {safePct}%
            </div>
        </div>
    );
}


function DoctorClinicCard({ doctor }) {
    const docInitials = (doctor.name || "Dr").split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
    return (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="doctor-clinic-card">
            <div className="flex items-center gap-4 pb-4 mb-3 border-b border-border/60">
                {doctor.image ? (
                    <img src={doctor.image} alt={doctor.name} className="w-14 h-14 rounded-full object-cover border border-indigo-200 shrink-0" />
                ) : (
                    <div className="w-14 h-14 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 grid place-items-center text-base font-semibold shrink-0">
                        {docInitials}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold truncate">{doctor.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                        {doctor.specialization || "Aligner specialist"}{doctor.qualifications ? ` · ${doctor.qualifications}` : ""}
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-primary/10 text-primary rounded-md px-2 py-0.5 shrink-0">
                    <CheckCircle size={11} weight="fill" /> Active
                </span>
            </div>
            <div className="space-y-1.5">
                {doctor.clinic_name && (
                    <RowItem icon={Building} label="Clinic" value={doctor.clinic_name} />
                )}
                {doctor.address && (
                    <RowItem icon={MapPin} label="Address" value={[doctor.address, doctor.city].filter(Boolean).join(", ")} />
                )}
                {doctor.phone && (
                    <RowItem icon={Phone} label="Phone" value={doctor.phone} />
                )}
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
                {doctor.address && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([doctor.clinic_name, doctor.address, doctor.city].filter(Boolean).join(", "))}`, "_blank")} data-testid="doctor-directions-btn">
                        <MapTrifold size={12} className="mr-1.5" /> Directions
                    </Button>
                )}
                {doctor.phone && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => window.location.assign(`tel:${doctor.phone}`)} data-testid="doctor-call-btn">
                        <Phone size={12} className="mr-1.5" /> Call
                    </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => window.location.assign("/patient/chat")} data-testid="doctor-message-btn">
                    <ChatCircleDots size={12} className="mr-1.5" /> Message
                </Button>
            </div>
        </div>
    );
}

function RowItem({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3 py-1.5">
            <div className="flex items-center gap-2 text-xs text-muted-foreground w-32 shrink-0">
                <Icon size={13} /> {label}
            </div>
            <div className="flex-1 text-sm font-medium text-foreground/90 break-words">{value}</div>
        </div>
    );
}


/* ─────────────────────── Dialogs ─────────────────────── */
function ChangePasswordDialog({ open, onClose }) {
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-sm" data-testid="change-password-dialog">
                <DialogHeader>
                    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center mb-2">
                        <Key size={20} weight="duotone" />
                    </div>
                    <DialogTitle>Change password</DialogTitle>
                    <DialogDescription>
                        Heads up — Invisique™ uses OTP-based sign-in. A backup password is optional and we&apos;ll wire this up soon.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function DeleteAccountDialog({ open, onClose }) {
    const [confirmText, setConfirmText] = useState("");
    const handleClose = () => { setConfirmText(""); onClose(); };
    const canDelete = confirmText === "DELETE";
    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-sm" data-testid="delete-account-dialog">
                <DialogHeader>
                    <div className="w-11 h-11 rounded-full bg-rose-100 text-rose-700 grid place-items-center mb-2">
                        <Trash size={20} weight="duotone" />
                    </div>
                    <DialogTitle>Delete your account?</DialogTitle>
                    <DialogDescription>
                        This permanently deletes your account, all treatment records, photos and appointments. <strong>This cannot be undone.</strong>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex gap-2.5 items-start p-3 rounded-md bg-amber-50 border border-amber-200">
                    <Info size={14} className="text-amber-700 mt-0.5 shrink-0" />
                    <p className="text-[12px] leading-relaxed text-amber-800">
                        Need to transfer to another clinic? Contact your doctor first to export your records.
                    </p>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Type <strong>DELETE</strong> to confirm</Label>
                    <Input
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="Type DELETE here…"
                        className="h-9"
                        data-testid="delete-account-confirm-input"
                    />
                </div>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={handleClose}>Keep account</Button>
                    <Button
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        disabled={!canDelete}
                        onClick={() => { handleClose(); toast("Deletion request sent — our team will contact you within 24 hrs."); }}
                        data-testid="delete-account-confirm-btn"
                    >
                        Delete account
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function SignOutDialog({ open, onClose, onConfirm }) {
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-sm" data-testid="signout-dialog">
                <DialogHeader>
                    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary grid place-items-center mb-2">
                        <SignOut size={20} weight="duotone" />
                    </div>
                    <DialogTitle>Sign out?</DialogTitle>
                    <DialogDescription>
                        You&apos;ll be returned to the Invisique™ website. Sign back in anytime with your phone number.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" size="sm" onClick={onClose}>Stay signed in</Button>
                    <Button size="sm" onClick={onConfirm} data-testid="signout-confirm-btn">Sign out</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
